import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiFetch } from '../utils/apiClient';
import API_URL from '../config/api';
import { setAppLanguage, AppLanguage, SUPPORTED_LANGUAGES } from '../i18n';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
}

export interface Vendor {
  id: number;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  approved_at?: string | null;
  preferred_language?: AppLanguage;
  profile_picture?: string;
  business_name?: string;
  bio?: string;
  city?: string;
  country?: string;
  commission_rate?: string;
  bank_name?: string;
  bank_account_name?: string;
  bank_account_number?: string;
  bank_branch?: string;
  shopify_vendor_name?: string;
  created_at: string;
  updated_at: string;
  inventory_items_count?: number;
}

export interface AppSettings {
  auto_sync: boolean;
  sync_mode?: 'inline' | 'background';
  email_enabled?: boolean;
  require_photo?: boolean;
  order_confirmation_minutes: number;
}

interface VendorContextType {
  user: User | null;
  vendor: Vendor | null;
  settings: AppSettings;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Signed-in vendor whose store has not been approved yet */
  isPendingVendor: boolean;
  pendingOrders: number;
  refreshPendingOrders: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const DEFAULT_SETTINGS: AppSettings = { auto_sync: false, order_confirmation_minutes: 30 };

const VendorContext = createContext<VendorContextType | undefined>(undefined);

export const useVendor = () => {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
};

interface VendorProviderProps {
  children: ReactNode;
}

export const VendorProvider: React.FC<VendorProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingOrders, setPendingOrders] = useState(0);

  const refreshToken = async (): Promise<boolean> => {
    const refreshTokenValue = localStorage.getItem('refresh_token');
    if (!refreshTokenValue) {
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/api/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshTokenValue }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      return true;
    } catch (err) {
      console.error('Token refresh failed:', err);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setVendor(null);
    setPendingOrders(0);
    setError(null);
  };

  const refreshPendingOrders = useCallback(async () => {
    try {
      const response = await apiFetch('/api/orders/pending-count/');
      if (response.ok) {
        const data = await response.json();
        setPendingOrders(data.pending || 0);
      }
    } catch {
      // non-critical
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch('/api/user/profile/');

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setUser(data.user);
      setVendor(data.vendor || null);
      setSettings({ ...DEFAULT_SETTINGS, ...(data.settings || {}) });
      setError(null);

      // Follow the language saved on the vendor profile, unless the user
      // has explicitly picked one in this browser.
      const saved = data.vendor?.preferred_language;
      let explicit: string | null = null;
      try {
        explicit = localStorage.getItem('bm_language');
      } catch {
        // ignore
      }
      if (!explicit && saved && (SUPPORTED_LANGUAGES as readonly string[]).includes(saved)) {
        setAppLanguage(saved);
      }

      refreshPendingOrders();
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [fetchProfile]);

  // Keep the pending-orders badge fresh while the app is open
  useEffect(() => {
    if (!user) return;
    const id = window.setInterval(refreshPendingOrders, 60_000);
    return () => window.clearInterval(id);
  }, [user, refreshPendingOrders]);

  const isAdmin = !!user && (user.is_staff || user.is_superuser);

  const value: VendorContextType = {
    user,
    vendor,
    settings,
    loading,
    error,
    isAuthenticated: !!user && !!localStorage.getItem('access_token'),
    isAdmin,
    isPendingVendor: !!user && !isAdmin && !!vendor && !vendor.is_active,
    pendingOrders,
    refreshPendingOrders,
    fetchProfile,
    logout,
    refreshToken,
  };

  return <VendorContext.Provider value={value}>{children}</VendorContext.Provider>;
};
